import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import cn from 'classnames';
// Types
import {
  GetStream_stream_mentions,
  GetStream_stream_hashtags,
} from 'api/streams/types/GetStream';
import {
  GetMediaPost_getMediaPost_VideoPost_mentions,
  GetMediaPost_getMediaPost_VideoPost_hashtags,
} from 'api/mediaPost/types/GetMediaPost';
// Helpers
import { parseForInnerHTML } from 'helpers/textEditor';
// Components
import MentionsView from 'components/MentionsView/MentionsView';
// UI3
import HashTags from 'ui3/HashTags/HashTags';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './VideoDetails.module.scss';

export interface VideoDetails {
  views: number;
  date: string;
  description: string | null;
  mentions: (
    | GetStream_stream_mentions
    | GetMediaPost_getMediaPost_VideoPost_mentions
  )[];
  hashtags: (
    | GetStream_stream_hashtags
    | GetMediaPost_getMediaPost_VideoPost_hashtags
  )[];
}

type VideoDetailsProps = {
  videoDetails: VideoDetails;
  streamDateTimeText?: string;
  // Stream's details on desktop are always expanded by default, don't have a toggle button and don't show tags
  isStream?: boolean;
  hashtags?: (
    | GetStream_stream_hashtags
    | GetMediaPost_getMediaPost_VideoPost_hashtags
  )[];
  // When true, component starts expanded and hides toggle button (used when parent controls expansion)
  startExpanded?: boolean;
  className?: string;
  // Disclaimer only applies to PPV streams, not free streams, interviews, or media posts
  showDisclaimer?: boolean;
};

const VideoDetails = ({
  videoDetails,
  streamDateTimeText,
  isStream = false,
  hashtags,
  startExpanded,
  className,
  showDisclaimer = false,
}: VideoDetailsProps): JSX.Element => {
  const isDesktopStream = !isMobile && isStream;
  const [isExpanded, setIsExpanded] = useState(
    startExpanded !== undefined ? startExpanded : isDesktopStream
  );
  // Show views when we have a value (including during live)
  const showViews = videoDetails.views != null;

  const handleToggleDescription = () => {
    setIsExpanded(!isExpanded);
  };

  // Use passed hashtags if provided, otherwise fall back to videoDetails.hashtags
  const tagsToShow = hashtags || videoDetails.hashtags;
  const showTags = tagsToShow && tagsToShow.length > 0;

  const showToggleButton = !isDesktopStream && startExpanded === undefined;

  return (
    <div className={cn(styles.videoDetails, className)}>
      <div className={styles.videoDetailsRow}>
        <div className={styles.viewsDateSection}>
          {showViews && (
            <span className={styles.viewsText}>
              <MillionsIcon name="eye" className={styles.eyeIcon} />
              {videoDetails.views} views
            </span>
          )}
          <span className={styles.dateText}>
            {streamDateTimeText || videoDetails.date}
          </span>
        </div>
      </div>

      {videoDetails.description && (
        <div className={styles.videoDetailsRow}>
          <div
            className={styles.descriptionText}
            dangerouslySetInnerHTML={{
              __html: parseForInnerHTML(videoDetails.description),
            }}
          ></div>
        </div>
      )}

      {isExpanded && (
        <>
          {videoDetails.mentions && videoDetails.mentions.length > 0 && (
            <div className={styles.mentionsSection}>
              <MentionsView mentions={videoDetails.mentions as any} size="sm" />
            </div>
          )}

          {showTags && (
            <>
              <hr className={styles.dividerLine} />
              <p className={styles.hashtagsLabel}>TAGS</p>
              <div className={styles.hashtagsSection}>
                <HashTags
                  tags={tagsToShow}
                  className={styles.inlineTags}
                  hashTagClassName={styles.smallHashTag}
                />
              </div>
            </>
          )}

          {showDisclaimer && (
            <>
              <hr className={styles.dividerLine} />
              <p className={styles.disclaimer}>
                {/* same text is used on StreamDetailsModal component, if you change it here, change it there too */}
                Disclaimer: MILLIONS.co served as the streaming platform and
                completed all required testing. The event&apos;s live feed and
                technical production were managed independently by a third-party
                provider.
              </p>
            </>
          )}
        </>
      )}

      {showToggleButton && (
        <div className={styles.actionsRow}>
          <button
            className={styles.toggleButton}
            onClick={handleToggleDescription}
            type="button"
          >
            {isExpanded ? 'Show Less' : 'Show More'}
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoDetails;
